<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from "vue";
import { ChevronDown } from "@lucide/vue";
import { v4 as uuidv4 } from "uuid";

import InputLabel from "./InputLabel.vue";
import { useListboxNavigation } from "./useListboxNavigation.composable";
import InfoIcon from "@/features/Tooltip/InfoIcon.vue";

type SelectOption = { name: string; value: any; disabled?: boolean };

// A disabled option is a heading, not a choice: it renders as a label row and arrow keys skip it.
type Row = { option: SelectOption; selectableIndex: number };

const props = defineProps<{
  value?: number | null | number | string | undefined;
  options: Array<SelectOption>;
  label?: string;
  isDisabled?: boolean;
  errorMsg?: string;
  width?: string;
  tooltip?: string;
  /** Lock icon beside the value, for a field disabled because an upstream choice constrains it. */
  lockTooltip?: string;
  placeholder?: string;
  required?: boolean;
  /** Accessible name for a select whose visible label lives outside this component. */
  ariaLabel?: string;
  /** `sm` is the compact 24px trigger for dense tool panels; default `md`. */
  size?: "md" | "sm";
  describedBy?: string;
}>();

// Emits the option value as a string, as the native select's `event.target.value` did.
const emits = defineEmits<{
  "update:value": [value: string];
}>();

const id = ref<string>(uuidv4());
const listboxId = computed(() => `${id.value}-listbox`);
const width = computed(() => props.width ?? "100%");
const isCompact = computed(() => props.size === "sm");

const triggerRef = ref<HTMLButtonElement | null>(null);
const panelRef = ref<HTMLElement | null>(null);

const rows = computed<Row[]>(() => {
  let selectableCount = 0;
  return (props.options ?? []).map((option) => ({ option, selectableIndex: option.disabled ? -1 : selectableCount++ }));
});

const selectableRows = computed(() => rows.value.filter((row) => row.selectableIndex >= 0));

const isSelected = (option: SelectOption) =>
  props.value !== null && props.value !== undefined && String(props.value) === String(option.value);

const selectedOption = computed(() => (props.options ?? []).find((option) => isSelected(option)) ?? null);

const optionId = (row: Row) => `${id.value}-option-${row.selectableIndex}`;

// Re-picking the current value is not a change, as with a native select; callers reset state on change.
const onCommit = (row: Row) => {
  if (isSelected(row.option)) return;
  emits("update:value", String(row.option.value));
};

const { root, isOpen, activeIndex, activeOptionId, open, close, commit, handleKeydown } = useListboxNavigation<Row>({
  items: selectableRows,
  optionId,
  onCommit,
  isDisabled: () => props.isDisabled ?? false,
  activeOnOpen: () =>
    Math.max(
      selectableRows.value.findIndex((row) => isSelected(row.option)),
      0
    ),
  selectOnly: true,
  panel: panelRef,
});

const onTriggerClick = () => {
  if (props.isDisabled) return;
  isOpen.value ? close() : open();
};

// ModalRoot closes the top modal on a window-level Escape, so an Escape that closes the list stops here.
const onTriggerKeydown = (event: KeyboardEvent) => {
  if (event.key === "Escape" && isOpen.value) event.stopPropagation();
  handleKeydown(event);
};

// Teleported so modals and scroll areas cannot clip it; fixed to the trigger, flipped up when short of room.
const GAP = 4;
const MARGIN = 8;
const PANEL_MAX_WIDTH = 384;
const panelStyle = ref<Record<string, string>>({});

const positionPanel = () => {
  if (!triggerRef.value || !panelRef.value) return;
  const trigger = triggerRef.value.getBoundingClientRect();
  const panelHeight = panelRef.value.offsetHeight;
  const spaceBelow = window.innerHeight - trigger.bottom - GAP - MARGIN;
  const opensUp = panelHeight > spaceBelow && trigger.top - GAP - MARGIN > spaceBelow;
  const top = opensUp ? Math.max(MARGIN, trigger.top - GAP - panelHeight) : trigger.bottom + GAP;
  // At least as wide as the trigger; may grow to fit long options, but never past the viewport's right edge.
  const maxWidth = Math.max(trigger.width, Math.min(PANEL_MAX_WIDTH, window.innerWidth - trigger.left - MARGIN));
  panelStyle.value = { top: `${top}px`, left: `${trigger.left}px`, minWidth: `${trigger.width}px`, maxWidth: `${maxWidth}px` };
};

const stopTracking = () => {
  window.removeEventListener("scroll", positionPanel, true);
  window.removeEventListener("resize", positionPanel);
};

watch(isOpen, async (opened) => {
  if (!opened) return stopTracking();
  await nextTick();
  positionPanel();
  window.addEventListener("scroll", positionPanel, true);
  window.addEventListener("resize", positionPanel);
});

onBeforeUnmount(stopTracking);
</script>

<template>
  <!-- style kept: width is an arbitrary CSS value passed as a prop (e.g. "50%", "200px") -->
  <div
    ref="root"
    class="flex flex-col"
    :style="{ width }">
    <div class="flex items-center gap-2">
      <InputLabel
        :id="id"
        :label="label"
        :required="required"
        :tooltip="tooltip" />
    </div>
    <div class="relative inline-block">
      <button
        :id="id"
        ref="triggerRef"
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        :aria-expanded="isOpen"
        :aria-controls="isOpen ? listboxId : undefined"
        :aria-activedescendant="activeOptionId"
        :aria-label="ariaLabel"
        :aria-required="required || undefined"
        :aria-invalid="!!errorMsg || undefined"
        :aria-describedby="describedBy"
        :disabled="isDisabled"
        class="focus-input flex w-full items-center justify-between border text-left leading-normal"
        :class="[
          isCompact ? 'rounded-4 t-xs h-6 py-0.5 pr-5' : 'rounded-8 t-small h-9 py-2 pr-8',
          isDisabled ? 'text-type-muted interactive-disabled bg-slate-200' : 'bg-slate-0 text-type-default',
          errorMsg ? 'border-app-red-500' : 'border-slate-300',
          lockTooltip ? 'pl-9' : isCompact ? 'pl-1.5' : 'pl-3',
        ]"
        @click="onTriggerClick"
        @keydown="onTriggerKeydown">
        <span
          v-if="selectedOption"
          class="truncate">
          {{ selectedOption.name }}
        </span>
        <span
          v-else
          class="text-type-muted truncate">
          {{ placeholder }}
        </span>
      </button>
      <div
        v-if="lockTooltip"
        class="absolute top-1/2 left-3 -translate-y-1/2">
        <InfoIcon :tooltip="lockTooltip">
          <font-awesome-icon
            icon="fa-solid fa-lock"
            class="icon-sm text-slate-700/70" />
        </InfoIcon>
      </div>
      <span
        class="text-type-muted pointer-events-none absolute top-0 right-0 flex h-full items-center"
        :class="isCompact ? 'pr-1.5' : 'pr-2'">
        <ChevronDown
          :class="isCompact ? 'icon-xs' : 'icon-sm'"
          :stroke-width="1.5"
          aria-hidden="true" />
      </span>
    </div>
    <p
      v-if="errorMsg"
      class="text-app-red-700 text-xs">
      {{ errorMsg }}
    </p>

    <!-- style kept: fixed coordinates measured from the trigger, which no utility class can express -->
    <!-- z-tooltip, as SearchSelectUser uses: ModalRoot stacks modals upward from z-modal. -->
    <Teleport to="body">
      <Transition name="fade-top-quick">
        <div
          v-if="isOpen"
          :id="listboxId"
          ref="panelRef"
          role="listbox"
          class="rounded-8 bg-elevated shadow-dropdown z-tooltip fixed max-h-60 w-max overflow-y-auto ring-1 ring-slate-200"
          :style="panelStyle">
          <template
            v-for="row in rows"
            :key="`${row.option.name}-${String(row.option.value)}`">
            <div
              v-if="row.selectableIndex < 0"
              role="presentation"
              class="text-type-muted t-sh6 bg-slate-100 py-1 tracking-wide uppercase"
              :class="isCompact ? 'px-2' : 'px-3'">
              {{ row.option.name }}
            </div>
            <div
              v-else
              :id="optionId(row)"
              role="option"
              :aria-selected="isSelected(row.option)"
              class="text-type-default flex w-full cursor-pointer items-center gap-2"
              :class="[
                isCompact ? 'px-2 py-1' : 'px-3 py-2',
                isSelected(row.option) ? (isCompact ? 't-caption-bold' : 't-small-bold') : isCompact ? 't-xs' : 't-small',
                { 'bg-slate-100': activeIndex === row.selectableIndex },
              ]"
              @mouseenter="activeIndex = row.selectableIndex"
              @mousedown.prevent="commit(row)">
              <span>{{ row.option.name }}</span>
            </div>
          </template>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>
